import { Inject, Injectable, PLATFORM_ID } from '@angular/core'
import { CmsService, EventService, LanguageService, Page, WindowRef } from '@spartacus/core'
import { BREAKPOINT, BreakpointService } from '@spartacus/storefront'
import { forkJoin, Observable } from 'rxjs'
import { CountryService } from '../../site-context/facade/country.service'
import { PageViewEvent } from './page-view-event'
import { distinctUntilChanged, filter, first, map, switchMap } from 'rxjs/operators'
import { MbNavigationService, NavigationNode } from '../navigation/mb-navigation.service'
import { NavigationEnd, Router } from '@angular/router'
import { MbContentPageMetaResolver } from './mb-content-page-meta-resolver'
import { ActiveCartService } from '@spartacus/cart/base/core'
import { Cart } from '@spartacus/cart/base/root'
import { isPlatformServer } from '@angular/common'

@Injectable({ providedIn: 'root' })
export class PageViewEventBuilder {

  private readonly businessSeg1 = 'Technology'
  private readonly businessSeg2 = 'Cell type'
  private readonly businessSeg3 = 'Research area'

  private readonly checkoutPattern = /\/checkout\//

  constructor (@Inject(PLATFORM_ID) protected platformId: Object,
               private windowRef: WindowRef,
               private languageService: LanguageService,
               private cartService: ActiveCartService,
               private breakpointService: BreakpointService,
               private countryService: CountryService,
               private cmsService: CmsService,
               private navigationService: MbNavigationService,
               private router: Router,
               private eventService: EventService,
               private contentPageMetaResolver: MbContentPageMetaResolver) {
  }

  init (): void {
    if (isPlatformServer(this.platformId)) {return}

    const pageViewEvent$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      switchMap(() => this.cmsService.getCurrentPage().pipe(first())),
      map(() => this.windowRef.location.pathname),
      distinctUntilChanged((oldUrl, newUrl) => oldUrl === newUrl),
      switchMap(() => this.buildPageViewEvent().pipe(first())),
      filter(event => Boolean(event?.site?.virtPath?.length))
    )

    this.eventService.register(PageViewEvent, pageViewEvent$)
  }

  buildPageViewEvent (): Observable<PageViewEvent> {
    return forkJoin([
      this.countryService.getActive().pipe(first()),
      this.languageService.getActive().pipe(first()),
      this.cartService.getActive().pipe(first()),
      this.breakpointService.breakpoint$.pipe(first()),
      this.cmsService.getCurrentPage().pipe(first()),
      this.navigationService.navigation$.pipe(first()),
      this.contentPageMetaResolver.resolveTitle().pipe(first())
    ]).pipe(
      map(([country, language, cart, breakpoint, page, navigation, title]) => {
        const previewId = (page as Page)?.properties?.previewId?.split('.')?.[0] ?? ''
        const cmsPage = this.navigationService.idMap?.[previewId]
        const currentNode = this.navigationService.resolvedIdMap.value.find(n => n?.id === previewId)
        const pageLabel = (page as Page)?.label || ''
        const isCheckout = this.checkoutPattern.test(pageLabel)
        // Content Group
        let contentGroup = (currentNode?.customData?.pt_breadcrumb ?? '').split('/')[1] ?? ''
        contentGroup = contentGroup.split('-').map((path: string = '') => path?.length ? `${path[0].toUpperCase()}${path.substring(1)}`
          : '').join(' ')
        const contentGroupIndex = (navigation as NavigationNode[])?.findIndex(navEntry => navEntry.id ===
          currentNode?.parentIds?.[0]) + 1

        const pageGoal = (page as Page)?.properties?.formData?.pt_page_goal?.value || undefined
        const label = cmsPage?.label?.replaceAll(' ', '-')?.toLowerCase() ?? ''
        let breadcrumb = (currentNode?.customData?.pt_breadcrumb ?? '').toLowerCase().replaceAll(' ', '-')
        breadcrumb = breadcrumb?.endsWith(label) ? `${breadcrumb}.html` : `${breadcrumb?.length ? `${breadcrumb}/` : ''}${label}.html`
        const tags = (page as Page)?.properties?.formData?.pt_tags?.value ?? []
        const pageViewEvent = new PageViewEvent()
        pageViewEvent.site = {
          virtPath: this.windowRef.location.pathname ?? '',
          country: country as string,
          language: language as string
        }
        if (!isCheckout) {
          pageViewEvent.site.pageType = this.getPageType((page as Page)?.properties?.fsPageTemplate ?? ''),
          pageViewEvent.site.virtTitle = (title as string ?? '').substring(0, (title as string).lastIndexOf('|') - 1),
          pageViewEvent.site.businessSeg1 = tags.filter((tag: any) => tag?.value?.target?.category === this.businessSeg1)
            .map((tag: any) => tag?.value?.target?.name ?? '')
            .join(' | ')
          pageViewEvent.site.businessSeg2 = tags.filter((tag: any) => tag?.value?.target?.category === this.businessSeg2)
            .map((tag: any) => tag?.value?.target?.name ?? '')
            .join(' | ')
          pageViewEvent.site.businessSeg3 = tags.filter((tag: any) => tag?.value?.target?.category === this.businessSeg3)
            .map((tag: any) => tag?.value?.target?.name ?? '')
            .join(' | ')
        }
        if (!!pageGoal) {
          pageViewEvent.site.pageGoal = pageGoal
        }
        pageViewEvent.store = {
          cart: ((cart as Cart)?.entries?.length ?? 0) > 0 ? 'f' : 'e'
        }
        pageViewEvent.system = {
          view: (breakpoint as BREAKPOINT)?.valueOf() ?? ''
        }
        if (isCheckout) {
           pageViewEvent.site.contentGroup1 = 'Checkout'
           pageViewEvent.site.contentGroup2 = ''
           pageViewEvent.site.contentGroup3 = ''
           pageViewEvent.site.contentGroup4 = ''
           pageViewEvent.site.contentGroup5 = ''

        } else {
          switch (contentGroupIndex) {
            case 1:
              pageViewEvent.site.contentGroup1 = contentGroup
              break
            case 2:
              pageViewEvent.site.contentGroup2 = contentGroup
              break
            case 3:
              pageViewEvent.site.contentGroup3 = contentGroup
              break
            case 4:
              pageViewEvent.site.contentGroup4 = contentGroup
              break
            case 5:
              pageViewEvent.site.contentGroup5 = contentGroup
              break
          }
        }
        return pageViewEvent
      })
    )
  }

  getPageType (pageType: string): string {
    switch (pageType) {
      case ('homepage'):
        return 'Homepage'
      case ('contentpage'):
        return 'Contentpage'
      case ('landingpage2template'):
        return 'LandingPage'
      case ('categorypage'):
        return 'CategoryPage'
      case ('backgroundpage'):
        return 'Page.BackgroundPage'
      case ('congresspage'):
        return 'CongressPage'
      case ('macshandbookpage'):
        return 'HandbookPage'
      case ('applicationpage'):
        return 'ApplicationPage'
      case ('applicationprotocolspage'):
        return 'Page.ProtocolDetail'
      case ('productdetailspagetemplate'):
        return 'PimProductFamilyPage'
      case ('manualpdp'):
        return 'PimProductFamilyPage'
      case ('miltenyiuniversity'):
        return 'MacsUniversity:Page.Content'
      case ('pouploadtemplate'):
        return 'PoUpload'
      default:
        return 'Page'
    }
  }
}
